/*
 * LifeDataGrid — wrapper global du DataGrid MUI v5 (community).
 *
 * Ajoute à TOUS les tableaux de l'application :
 *  - le redimensionnement des colonnes (glisser le séparateur dans l'en-tête,
 *    double-clic sur le séparateur pour réinitialiser la largeur) ;
 *  - le déplacement des colonnes (glisser l'en-tête vers une autre colonne) ;
 *  - la persistance des préférences par page (localStorage).
 *
 * Utilisation : importer depuis 'Components/LifeDataGrid' au lieu de
 * '@mui/x-data-grid' (tous les autres exports MUI sont réexportés à l'identique).
 * Prop optionnelle : disableColumnCustomization pour désactiver le comportement.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid'

export * from '@mui/x-data-grid'

const STORAGE_PREFIX = 'lifegrid-v1'
const MIN_COL_WIDTH = 50
const DRAG_THRESHOLD_PX = 6

/* ------------------------------------------------------------------ */
/* Styles injectés une seule fois                                      */
/* ------------------------------------------------------------------ */
let stylesInjected = false
const injectStyles = () => {
  if (stylesInjected || typeof document === 'undefined') return
  stylesInjected = true
  const style = document.createElement('style')
  style.setAttribute('data-lifegrid', 'true')
  style.textContent = `
    .lifegrid-root .MuiDataGrid-columnSeparator {
      pointer-events: auto !important;
      cursor: col-resize;
    }
    .lifegrid-root .MuiDataGrid-columnSeparator:hover .MuiSvgIcon-root {
      color: #1976d2;
    }
    .lifegrid-root .MuiDataGrid-columnHeaderTitleContainer {
      cursor: grab;
    }
    .lifegrid-root .lifegrid-drop-target {
      background: rgba(25, 118, 210, 0.12);
      box-shadow: inset 2px 0 0 #1976d2;
    }
    .lifegrid-root .lifegrid-dragging-header {
      opacity: 0.4;
    }
  `
  document.head.appendChild(style)
}

/* ------------------------------------------------------------------ */
/* Persistance                                                         */
/* ------------------------------------------------------------------ */
const storageKeyFor = (columns) => {
  const path =
    typeof window !== 'undefined'
      ? window.location.pathname.replace(/\d+/g, ':id')
      : ''
  const fields = (columns || [])
    .map((c) => c.field)
    .filter((f) => f && !f.startsWith('__'))
    .sort()
    .join('|')
  const raw = `${path}::${fields}`
  let hash = 0
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash * 31 + raw.charCodeAt(i)) | 0
  }
  return `${STORAGE_PREFIX}:${path}:${hash}`
}

const loadPrefs = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || {}
  } catch (e) {
    return {}
  }
}

const savePrefs = (key, prefs) => {
  try {
    localStorage.setItem(key, JSON.stringify(prefs))
  } catch (e) {
    /* stockage indisponible : préférences non persistées, sans blocage */
  }
}

/* Avale le clic qui suit un drag pour ne pas déclencher le tri */
const swallowNextClick = () => {
  const swallow = (e) => {
    e.stopPropagation()
    e.preventDefault()
    window.removeEventListener('click', swallow, true)
  }
  window.addEventListener('click', swallow, true)
  setTimeout(() => window.removeEventListener('click', swallow, true), 150)
}

/* ------------------------------------------------------------------ */
/* Composant                                                           */
/* ------------------------------------------------------------------ */
export const DataGrid = ({ columns, disableColumnCustomization = false, ...rest }) => {
  injectStyles()

  const storageKey = useMemo(() => storageKeyFor(columns), [columns])
  const [prefs, setPrefs] = useState(() => loadPrefs(storageKey))
  const rootRef = useRef(null)
  const orderRef = useRef([])

  useEffect(() => {
    setPrefs(loadPrefs(storageKey))
  }, [storageKey])

  const commit = useCallback(
    (updater) => {
      setPrefs((prev) => {
        const next = updater(prev)
        savePrefs(storageKey, next)
        return next
      })
    },
    [storageKey]
  )

  const effectiveColumns = useMemo(() => {
    if (!columns || disableColumnCustomization) return columns
    const widths = prefs.widths || {}
    let cols = columns.map((col) => {
      const stored = widths[col.field]
      if (stored) {
        const { flex, ...restCol } = col
        return { ...restCol, width: stored }
      }
      return col
    })
    const order = prefs.order || []
    if (order.length) {
      const rank = new Map(order.map((f, i) => [f, i]))
      const base = new Map(columns.map((c, i) => [c.field, i]))
      cols = [...cols].sort((a, b) => {
        const ra = rank.has(a.field) ? rank.get(a.field) : 100000 + base.get(a.field)
        const rb = rank.has(b.field) ? rank.get(b.field) : 100000 + base.get(b.field)
        return ra - rb
      })
    }
    return cols
  }, [columns, prefs, disableColumnCustomization])

  orderRef.current = (effectiveColumns || []).map((c) => c.field)

  /* ----------------------- interactions souris ----------------------- */
  const handleMouseDown = useCallback(
    (e) => {
      if (disableColumnCustomization || e.button !== 0) return
      const headerCell = e.target.closest('.MuiDataGrid-columnHeader')
      if (!headerCell || !rootRef.current || !rootRef.current.contains(headerCell)) return
      const field = headerCell.getAttribute('data-field')
      if (!field || field.startsWith('__')) return

      const separator = e.target.closest('.MuiDataGrid-columnSeparator')

      if (separator) {
        /* ------- redimensionnement ------- */
        e.preventDefault()
        e.stopPropagation()
        const startX = e.clientX
        const startWidth = headerCell.offsetWidth
        let raf = null
        const onMove = (ev) => {
          const width = Math.max(MIN_COL_WIDTH, startWidth + (ev.clientX - startX))
          if (raf) return
          raf = requestAnimationFrame(() => {
            raf = null
            commit((p) => ({ ...p, widths: { ...(p.widths || {}), [field]: width } }))
          })
        }
        const onUp = () => {
          window.removeEventListener('mousemove', onMove)
          window.removeEventListener('mouseup', onUp)
          document.body.style.cursor = ''
          document.body.style.userSelect = ''
          swallowNextClick()
        }
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
        return
      }

      /* ignorer le menu colonne, les cases à cocher, les champs de saisie */
      if (e.target.closest('.MuiDataGrid-menuIcon') || e.target.closest('input')) return

      /* ------- réordonnancement (au-delà d'un petit seuil) ------- */
      const startX = e.clientX
      let dragging = false
      let targetField = null
      let highlighted = null

      const clearHighlight = () => {
        if (highlighted) {
          highlighted.classList.remove('lifegrid-drop-target')
          highlighted = null
        }
      }

      const onMove = (ev) => {
        if (!dragging && Math.abs(ev.clientX - startX) < DRAG_THRESHOLD_PX) return
        if (!dragging) {
          dragging = true
          document.body.style.cursor = 'grabbing'
          document.body.style.userSelect = 'none'
          headerCell.classList.add('lifegrid-dragging-header')
        }
        const under = document.elementFromPoint(ev.clientX, ev.clientY)
        const cell = under && under.closest && under.closest('.MuiDataGrid-columnHeader')
        clearHighlight()
        targetField = null
        if (cell && rootRef.current && rootRef.current.contains(cell)) {
          const f = cell.getAttribute('data-field')
          if (f && !f.startsWith('__') && f !== field) {
            targetField = f
            highlighted = cell
            cell.classList.add('lifegrid-drop-target')
          }
        }
      }

      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        clearHighlight()
        headerCell.classList.remove('lifegrid-dragging-header')
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        if (dragging) swallowNextClick()
        if (dragging && targetField && targetField !== field) {
          commit((p) => {
            const order = [...orderRef.current]
            const from = order.indexOf(field)
            const to = order.indexOf(targetField)
            if (from === -1 || to === -1) return p
            order.splice(to, 0, order.splice(from, 1)[0])
            return { ...p, order }
          })
        }
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [commit, disableColumnCustomization]
  )

  /* double-clic sur un séparateur : réinitialise la largeur de la colonne */
  const handleDoubleClick = useCallback(
    (e) => {
      if (disableColumnCustomization) return
      const separator = e.target.closest('.MuiDataGrid-columnSeparator')
      if (!separator) return
      const headerCell = e.target.closest('.MuiDataGrid-columnHeader')
      const field = headerCell && headerCell.getAttribute('data-field')
      if (!field) return
      e.stopPropagation()
      commit((p) => {
        const widths = { ...(p.widths || {}) }
        delete widths[field]
        return { ...p, widths }
      })
    },
    [commit, disableColumnCustomization]
  )

  return (
    <div
      ref={rootRef}
      className="lifegrid-root"
      style={{ display: 'contents' }}
      onMouseDownCapture={handleMouseDown}
      onDoubleClickCapture={handleDoubleClick}
    >
      <MuiDataGrid columns={effectiveColumns} {...rest} />
    </div>
  )
}

export default DataGrid
